import {Button, Card, Grid, makeStyles, Typography} from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import {chain} from 'lodash';
import React, {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {formatCurrency} from '../components/CostDetail';
import IncrementingNumberInput from '../components/IncrementingNumberInput';
import {FlexBreakSmall, useIsSmall} from '../components/MediaHelpers';
import Table, {TableColumn} from '../components/Table';
import {OrderContext} from '../OrderContext';
import {ExchangeItem, MaterialBatchCollection} from '../OrderContext/types';
import {ExchangeItemContext} from './ExchangeItemContext';

const useStyles = makeStyles(theme => ({
    card: {
        padding: 15,
        background: theme.palette.grey[100],
        border: `1px solid ${theme.palette.grey[600]}`
    }
}))

type ExchangeItemProps = { items: MaterialBatchCollection[] }

const ReturnItems: React.FC<ExchangeItemProps> = ({items}) => {
    const classes = useStyles()
    const {purchases, setPurchases, order} = useContext(OrderContext)
    const {setShowSearch} = useContext(ExchangeItemContext)
    const isSmall = useIsSmall()
    const {t} = useTranslation()

    const handleClick = () => {
        setShowSearch(true)
    }

    const columns: TableColumn<ExchangeItem>[] = [
        {title: t('description'), property: 'shortDescription', flex: 2},
        {title: t('material_number'), property: 'productId'},
        {title: t('unit_price'), property: 'materialPrice'},
        {
            title: t('quantity'),
            property: 'quantity',
            flex: 3,
            render: (q, {productId}) =>
                <IncrementingNumberInput
                    size='small'
                    style={{width: isSmall ? '100%' : undefined, maxWidth: isSmall ? undefined : 200}}
                    value={q}
                    onChange={v => setPurchases(purchases.map(p => p.productId === productId ? {
                        ...p,
                        quantity: v as number
                    } : p))}
                />

        },
        {
            title: t('total'),
            property: 'materialPrice',
            render: (_, {materialPrice, quantity}) => formatCurrency(parseFloat(materialPrice || '') * quantity)
        },
        {
            property: 'materialPrice',
            render: (_, data) =>
                <Button
                    variant="outlined"
                    color="secondary"
                    size='small'
                    style={{width: isSmall ? '100%' : undefined}}
                    startIcon={<HighlightOffIcon/>}
                    onClick={() => setPurchases(purchases.filter(p => p.productId !== data.productId))}
                >
                    {t('remove')}
                </Button>
        }
    ]
    
    return (
        <>
            <Grid item xs={12}>
                {
                    !isSmall &&
                    <Card className={classes.card} style={{width: '100%'}}>
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: 10}}>
                                <div style={{flex: 2}}><p style={{margin: '0 0 10px'}}><b>{t('description')}</b></p>
                                </div>
                                <div style={{flex: 1}}><p style={{margin: '0 0 10px'}}><b>{t('material_number')}</b></p>
                                </div>
                                <div style={{flex: 1}}><p style={{margin: '0 0 10px'}}><b>{t('lot_number')}</b></p>
                                </div>
                                <div style={{flex: 1}}><p style={{margin: '0 0 10px'}}><b>{t('invoice')}</b></p></div>
                                <div style={{flex: 1}}><p style={{margin: '0 0 10px'}}><b>{t('quantity')}</b></p></div>
                                <div style={{flex: 1}}><p style={{margin: '0 0 10px'}}>
                                    <b>{t('price_in', {currency: items[0].currency})}</b></p></div>
                            </div>

                            {
                                items.map((item, i) =>
                                    <div key={i} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        marginBottom: 10
                                    }}>
                                        <div style={{flex: 2}}>
                                            <p style={{marginBottom: 0, marginTop: 0}}>{item.description}</p>
                                        </div>
                                        <div style={{flex: 1}}>
                                            <p style={{marginBottom: 0, marginTop: 0}}>
                                                {item.material}
                                            </p>
                                        </div>
                                        <div style={{flex: 1}}>
                                            <p style={{marginBottom: 0, marginTop: 0}}>
                                                {item.batch}
                                            </p>
                                        </div>
                                        <div style={{flex: 1}}>
                                            {
                                                chain(order[`${item.material}//${item.batch}`].action)
                                                    .map((a, j) =>
                                                        <p key={j} style={{marginBottom: 0, marginTop: 0}}>
                                                            {a.invoiceNumber}
                                                        </p>)
                                                    .value()
                                            }
                                        </div>
                                        <div style={{flex: 1}}><p
                                            style={{marginBottom: 0, marginTop: 0}}>{item.quantity}</p>
                                        </div>
                                        <div style={{flex: 1}}>
                                            <p style={{marginBottom: 0, marginTop: 0}}>
                                                {formatCurrency(parseFloat(item.price) * (item.quantity || 0))}
                                            </p>
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </Card>
                }

                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    marginBottom: 30,
                    marginTop: 30,
                    gap: 15
                }}>
                    <div>
                        <Typography variant='h5'>{t('ordering_in_exchange')}:</Typography>
                    </div>
                    <FlexBreakSmall/>
                    <div style={{justifyContent: 'right'}}>
                        <Button color='primary' variant='contained'
                                onClick={handleClick}>{t('add_exchange_item')}</Button>
                    </div>
                </div>

                {purchases.length !== 0 && <Table columns={columns} data={purchases}/>}
                {purchases.length === 0 && <div style={{textAlign: 'center', marginTop: 90}}></div>}
            </Grid>
        </>
    )
}

const ExchangeList = () => {
    const {order} = useContext(OrderContext)
    const items = useMemo(() => chain(order).reduce<MaterialBatchCollection[]>((acc, item) => [...acc, item], []).value(), [order])

    return (
        <Grid item container xs={12}>
            <ReturnItems items={items}/>
        </Grid>
    )
}

export default ExchangeList
